import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Ticket, Plus, Users, CheckCircle, Clock, 
  Edit2, Trash2, Download, QrCode, PoundSterling
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

export default function EventTicketsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [editingTicketType, setEditingTicketType] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    name: '',
    description: '',
    price: '',
    capacity: '',
    max_per_order: '10',
    is_active: true
  });

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      // Fetch event details
      const eventRes = await fetch(`${API_URL}/api/events/${eventId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      }

      // Fetch ticket types
      const typesRes = await fetch(`${API_URL}/api/tickets/types/event/${eventId}`);
      if (typesRes.ok) {
        const typesData = await typesRes.json();
        setTicketTypes(typesData);
      }

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/tickets/checkin-stats/${eventId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast.error('Failed to load event data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicketType = async () => {
    if (!ticketForm.name || !ticketForm.price || !ticketForm.capacity) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/tickets/types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_id: eventId,
          name: ticketForm.name,
          description: ticketForm.description,
          price: parseFloat(ticketForm.price),
          capacity: parseInt(ticketForm.capacity),
          max_per_order: parseInt(ticketForm.max_per_order) || 10,
          is_active: ticketForm.is_active
        })
      });

      if (res.ok) {
        toast.success('Ticket type created successfully');
        setShowTicketDialog(false);
        resetForm();
        fetchEventData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create ticket type');
      }
    } catch (error) {
      toast.error('Failed to create ticket type');
    }
  };

  const handleUpdateTicketType = async () => {
    if (!ticketForm.name || !ticketForm.price || !ticketForm.capacity) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/tickets/types/${editingTicketType.ticket_type_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: ticketForm.name,
          description: ticketForm.description,
          price: parseFloat(ticketForm.price),
          capacity: parseInt(ticketForm.capacity),
          max_per_order: parseInt(ticketForm.max_per_order) || 10,
          is_active: ticketForm.is_active
        })
      });

      if (res.ok) {
        toast.success('Ticket type updated');
        setShowTicketDialog(false);
        setEditingTicketType(null);
        resetForm();
        fetchEventData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update ticket type');
      }
    } catch (error) {
      toast.error('Failed to update ticket type');
    }
  };

  const handleDeleteTicketType = async (ticketTypeId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/types/${ticketTypeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Ticket type deleted');
        fetchEventData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete ticket type');
      }
    } catch (error) {
      toast.error('Failed to delete ticket type');
    }
  };

  const openEditDialog = (ticketType) => {
    setEditingTicketType(ticketType);
    setTicketForm({
      name: ticketType.name,
      description: ticketType.description || '',
      price: ticketType.price.toString(),
      capacity: ticketType.capacity.toString(),
      max_per_order: (ticketType.max_per_order || 10).toString(),
      is_active: ticketType.is_active
    });
    setShowTicketDialog(true);
  };

  const resetForm = () => {
    setTicketForm({
      name: '',
      description: '',
      price: '',
      capacity: '',
      max_per_order: '10',
      is_active: true
    });
    setEditingTicketType(null);
  };

  const exportAttendees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/attendees/${eventId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (!data.attendees || data.attendees.length === 0) {
          toast.info('No attendees to export yet');
          return;
        }
        
        const headers = ['Name', 'Email', 'Ticket Type', 'Quantity', 'Status', 'Checked In', 'Purchase Date'];
        const rows = data.attendees.map(a => [
          a.attendee_name || a.buyer_name || '',
          a.buyer_email || '',
          a.ticket_type_name || '',
          a.quantity || 1,
          a.status || '',
          a.checked_in ? 'Yes' : 'No',
          a.created_at ? new Date(a.created_at).toLocaleDateString() : ''
        ]);
        
        const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendees-${eventId}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.attendees.length} attendees`);
      }
    } catch (error) {
      toast.error('Failed to export attendees');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Event not found</h2>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Manage Tickets</h1>
                <p className="text-sm text-slate-500">{event.title}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to={`/events/${eventId}/checkin`}>
                <Button variant="outline" size="sm">
                  <QrCode className="w-4 h-4 mr-2" />
                  Check-In Scanner
                </Button>
              </Link>
              <Button variant="outline" size="sm" onClick={exportAttendees}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <Ticket className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_tickets || 0}</p>
                  <p className="text-sm text-slate-500">Total Sold</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.checked_in || 0}</p>
                  <p className="text-sm text-slate-500">Checked In</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.remaining || 0}</p>
                  <p className="text-sm text-slate-500">Remaining</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <PoundSterling className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">£{(stats.total_revenue || 0).toFixed(2)}</p>
                  <p className="text-sm text-slate-500">Revenue</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Ticket Types Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-900">Ticket Types</h2>
            <Button 
              onClick={() => {
                resetForm();
                setShowTicketDialog(true);
              }}
              className="bg-violet-600 hover:bg-violet-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Ticket Type
            </Button>
          </div>

          {ticketTypes.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
              <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No ticket types yet</h3>
              <p className="text-slate-500 mb-4">Add your first ticket type to start selling tickets</p>
              <Button 
                onClick={() => {
                  resetForm();
                  setShowTicketDialog(true);
                }}
                className="bg-violet-600 hover:bg-violet-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Ticket Type
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {ticketTypes.map((tt) => (
                <div 
                  key={tt.ticket_type_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-900">{tt.name}</h3>
                      {!tt.is_active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                    {tt.description && (
                      <p className="text-sm text-slate-500 mt-1">{tt.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-slate-600">
                        <strong>£{tt.price.toFixed(2)}</strong>
                      </span>
                      <span className="text-slate-500">
                        {tt.sold_count || 0} / {tt.capacity} sold
                      </span>
                      <span className="text-slate-500">
                        Max {tt.max_per_order || 10} per order
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(tt)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Ticket Type</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete &quot;{tt.name}&quot;? This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteTicketType(tt.ticket_type_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Create/Edit Ticket Type Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTicketType ? 'Edit Ticket Type' : 'Create Ticket Type'}
            </DialogTitle>
            <DialogDescription>
              {editingTicketType 
                ? 'Update the ticket type details'
                : 'Add a new ticket type for this event'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={ticketForm.name}
                onChange={(e) => setTicketForm({ ...ticketForm, name: e.target.value })}
                placeholder="e.g., General Admission, VIP, Early Bird"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                placeholder="What's included with this ticket?"
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price (£) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={ticketForm.price}
                  onChange={(e) => setTicketForm({ ...ticketForm, price: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  value={ticketForm.capacity}
                  onChange={(e) => setTicketForm({ ...ticketForm, capacity: e.target.value })}
                  placeholder="100"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="max_per_order">Max Per Order</Label>
              <Input
                id="max_per_order"
                type="number"
                min="1"
                max="50"
                value={ticketForm.max_per_order}
                onChange={(e) => setTicketForm({ ...ticketForm, max_per_order: e.target.value })}
                placeholder="10"
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Active (available for sale)</Label>
              <Switch
                id="is_active"
                checked={ticketForm.is_active}
                onCheckedChange={(checked) => setTicketForm({ ...ticketForm, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={editingTicketType ? handleUpdateTicketType : handleCreateTicketType}
              className="bg-violet-600 hover:bg-violet-500"
            >
              {editingTicketType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
